// src/utils/tasks/todoIntroPrompt.js
/**
 * One-shot hand-off flag for the "Your To do list" introduction.
 *
 * The onboarding form is where we learn that a user has just completed their
 * first profile submission, but the introduction itself belongs on the Home
 * page — the form redirects there (or to whichever recording flow the user came
 * from), so the popup must survive that navigation and fire on the next Home
 * render instead of interrupting the redirect.
 *
 * The flag is cleared when the user acknowledges the popup, NOT when Home reads
 * it. Reading is therefore repeatable: a Home remount (route churn, a auth
 * re-check, React's development double-effect) re-shows a popup the user has
 * not dismissed yet, rather than silently swallowing it.
 *
 * Storage is per-user and per-tab so a second account signing in on a shared
 * device never inherits another user's pending introduction.
 */

const STORAGE_KEY_PREFIX = "pdx:todoIntroPending:";

function getStorageKey(uid) {
  return typeof uid === "string" && uid ? `${STORAGE_KEY_PREFIX}${uid}` : null;
}

/**
 * Records that `uid` finished onboarding and still needs the introduction.
 *
 * @param {string} uid
 * @returns {void}
 */
export function markTodoIntroPending(uid) {
  const key = getStorageKey(uid);
  if (!key) return;

  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Private-mode/quota failures only cost the user an optional popup.
  }
}

/**
 * Whether `uid` still has an unacknowledged introduction waiting.
 *
 * @param {string} uid
 * @returns {boolean}
 */
export function isTodoIntroPending(uid) {
  const key = getStorageKey(uid);
  if (!key) return false;

  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/**
 * Clears the flag once the user has seen and acknowledged the introduction.
 *
 * @param {string} uid
 * @returns {void}
 */
export function clearTodoIntroPending(uid) {
  const key = getStorageKey(uid);
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to do — a stale flag costs at most one extra popup this session.
  }
}
